import { useState } from "react"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import type { TimeClockSettings } from "../../Objects/TimeClock/TimeClockSettings"
import type { SaveTimeClockPin } from "../../Objects/TimeClock/SaveTimeClockPin"
import type { TimeClockPunchPayload } from "../../Objects/TimeClock/TimeClockPunchPayload"
import type { TimeClockLaborCost } from "../../Objects/TimeClock/TimeClockLaborCost"
import type { TimeClockQrCode } from "../../Objects/TimeClock/TimeClockQrCode"
import type { TimeClockReport } from "../../Objects/TimeClock/TimeClockReport"
import type { CreateTimeClockAdjustment } from "../../Objects/TimeClock/CreateTimeClockAdjustment"
import type { ReviewTimeClockAdjustment } from "../../Objects/TimeClock/ReviewTimeClockAdjustment"
import type { TimeClockAdjustment } from "../../Objects/TimeClock/TimeClockAdjustment"
import type { TimeClockLiveEmployee } from "../../Objects/TimeClock/TimeClockLiveEmployee"
import type { TimeClockManagement } from "../../Objects/TimeClock/TimeClockManagement"
import type { SaveEmployeeWorkSchedule } from "../../Objects/TimeClock/SaveEmployeeWorkSchedule"
import type { EmployeeWorkSchedule } from "../../Objects/TimeClock/EmployeeWorkSchedule"
import type { TimeClockStatus } from "../../Objects/TimeClock/TimeClockStatus"
import type { TimeClockEntry } from "../../Objects/TimeClock/TimeClockEntry"
import type { TimeClockAdjustmentStatusType } from "../../Objects/TimeClock/TimeClockAdjustmentStatusType"
import {
  DownloadFileRequest,
  GetRequest,
  PostFormRequest,
  PostRequest,
  PutRequest,
} from "../../services/ApiClientService"

export const useTimeClock = () => {
  const { t } = useTranslation("general")

  const { setError } = useError()

  const [isLoading, setIsLoading] = useState(false)

  const run = async <T>(operation: () => Promise<T>) => {
    setIsLoading(true)

    try {
      return await operation()
    } catch (requestError) {
      const message =
        requestError instanceof Error && requestError.message.trim()
          ? requestError.message
          : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return undefined
    } finally {
      setIsLoading(false)
    }
  }

  const getMyStatus = () =>
    run(async () => {
      const response = await GetRequest<TimeClockStatus>(API_ROUTES.TIME_CLOCK.ME)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const punch = (payload: TimeClockPunchPayload) =>
    run(async () => {
      const response = await PostFormRequest<TimeClockEntry>(API_ROUTES.TIME_CLOCK.ME_PUNCH, payload)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const getMyReport = (startDate: string, endDate: string) =>
    run(async () => {
      const response = await GetRequest<TimeClockReport>(API_ROUTES.TIME_CLOCK.ME_REPORT(startDate, endDate))

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const getMyAdjustments = () =>
    run(async () => {
      const response = await GetRequest<TimeClockAdjustment[]>(API_ROUTES.TIME_CLOCK.ME_ADJUSTMENTS)

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const createMyAdjustment = (request: CreateTimeClockAdjustment) =>
    run(async () => {
      const response = await PostRequest<TimeClockAdjustment>(API_ROUTES.TIME_CLOCK.ME_ADJUSTMENTS, request)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const getManagement = () =>
    run(async () => {
      const response = await GetRequest<TimeClockManagement>(API_ROUTES.TIME_CLOCK.MANAGEMENT)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const saveSettings = (request: TimeClockSettings) =>
    run(async () => {
      const response = await PutRequest<TimeClockSettings, TimeClockSettings>(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_SETTINGS,
        request,
      )

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const saveSchedule = (request: SaveEmployeeWorkSchedule) =>
    run(async () => {
      const response = await PutRequest<EmployeeWorkSchedule[], SaveEmployeeWorkSchedule>(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_SCHEDULES,
        request,
      )

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const savePin = (request: SaveTimeClockPin) =>
    run(async () => {
      const response = await PutRequest<void, SaveTimeClockPin>(API_ROUTES.TIME_CLOCK.MANAGEMENT_PIN, request)

      if (!response.success) throw new Error(response.message)

      return true
    })

  const getEntries = (startDate: string, endDate: string, employeeId?: string) =>
    run(async () => {
      const response = await GetRequest<TimeClockEntry[]>(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_ENTRIES(startDate, endDate, employeeId),
      )

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const reviewEntry = (id: number, request: ReviewTimeClockAdjustment) =>
    run(async () => {
      const response = await PostRequest<TimeClockEntry>(API_ROUTES.TIME_CLOCK.MANAGEMENT_REVIEW_ENTRY(id), request)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const getLive = () =>
    run(async () => {
      const response = await GetRequest<TimeClockLiveEmployee[]>(API_ROUTES.TIME_CLOCK.MANAGEMENT_LIVE)

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const getAdjustments = (status?: TimeClockAdjustmentStatusType, employeeId?: string) =>
    run(async () => {
      const response = await GetRequest<TimeClockAdjustment[]>(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_ADJUSTMENTS(status, employeeId),
      )

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const createManagementAdjustment = (request: CreateTimeClockAdjustment) =>
    run(async () => {
      const response = await PostRequest<TimeClockAdjustment>(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_CREATE_ADJUSTMENT,
        request,
      )

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const reviewAdjustment = (id: number, request: ReviewTimeClockAdjustment) =>
    run(async () => {
      const response = await PostRequest<TimeClockAdjustment>(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_REVIEW_ADJUSTMENT(id),
        request,
      )

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const getReport = (employeeId: string, startDate: string, endDate: string) =>
    run(async () => {
      const response = await GetRequest<TimeClockReport>(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_REPORT(employeeId, startDate, endDate),
      )

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const downloadReportPdf = (employeeId: string, startDate: string, endDate: string) =>
    run(async () => {
      await DownloadFileRequest(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_REPORT_PDF(employeeId, startDate, endDate),
        `espelho-de-ponto-${startDate}-a-${endDate}.pdf`,
      )

      return true
    })

  const downloadReportCsv = (startDate: string, endDate: string, employeeId?: string) =>
    run(async () => {
      await DownloadFileRequest(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_REPORT_CSV(startDate, endDate, employeeId),
        `marcacoes-de-ponto-${startDate}-a-${endDate}.csv`,
      )

      return true
    })

  const getLaborCost = (startDate: string, endDate: string) =>
    run(async () => {
      const response = await GetRequest<TimeClockLaborCost[]>(
        API_ROUTES.TIME_CLOCK.MANAGEMENT_LABOR_COST(startDate, endDate),
      )

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const getQrCode = () =>
    run(async () => {
      const response = await GetRequest<TimeClockQrCode>(API_ROUTES.TIME_CLOCK.MANAGEMENT_QR_CODE)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  /**
   * Busca o código rotativo público, sem exibir erro global quando o restaurante ainda não o habilitou.
   */
  const getPublicQrCode = async (slug: string) => {
    const response = await GetRequest<TimeClockQrCode>(API_ROUTES.TIME_CLOCK.PUBLIC_QR_CODE(slug))

    return response.success
      ? { data: response.data, message: undefined }
      : { data: undefined, message: response.message }
  }

  return {
    isLoading,
    getMyStatus,
    punch,
    getMyReport,
    getMyAdjustments,
    createMyAdjustment,
    getManagement,
    saveSettings,
    saveSchedule,
    savePin,
    getEntries,
    reviewEntry,
    getLive,
    getAdjustments,
    createManagementAdjustment,
    reviewAdjustment,
    getReport,
    downloadReportPdf,
    downloadReportCsv,
    getLaborCost,
    getQrCode,
    getPublicQrCode,
  }
}
